/**
 * The two card rules that live on the `system` row and that the rider app
 * combines into "does starting a rental here need a card?":
 *
 *   needsCard = (hasRates AND billing AND NOT allowRentalWithoutCard) OR requireCreditCard
 *
 * Read from ant (the settings REST payload never carried
 * `allow_rental_without_card`); written back through the settings PUT like
 * every other rental setting.
 *
 * Note: Run `npm run relay` after editing the GraphQL query.
 */
import { fetchQuery } from 'react-relay';
import { useState, useEffect, useCallback } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type { useSystemCardRulesRelayQuery as UseSystemCardRulesRelayQuery } from './__generated__/useSystemCardRulesRelayQuery.graphql';

const CardRulesQuery = graphql`
  query useSystemCardRulesRelayQuery($id: ID!) {
    getSystem(id: $id) {
      id
      requireCreditCard
      allowRentalWithoutCard
    }
  }
`;

export interface SystemCardRules {
  requireCreditCard: boolean;
  allowRentalWithoutCard: boolean;
}

export function useSystemCardRulesRelay(systemId: string | undefined) {
  const [rules, setRules] = useState<SystemCardRules | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(systemId));
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  useEffect(() => {
    if (!systemId) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    fetchQuery<UseSystemCardRulesRelayQuery>(relayEnvironment, CardRulesQuery, {
      id: systemId,
    })
      .toPromise()
      .then((result: UseSystemCardRulesRelayQuery['response'] | undefined) => {
        if (cancelled) return;
        const system = result?.getSystem;
        setRules(
          system
            ? {
                requireCreditCard: system.requireCreditCard === true,
                allowRentalWithoutCard: system.allowRentalWithoutCard === true,
              }
            : null
        );
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const errorObj =
          err instanceof Error ? err : new Error('Failed to fetch system card rules');
        setError(errorObj);
        console.error('[useSystemCardRulesRelay] fetch error:', errorObj);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [systemId, fetchCount]);

  const refetch = useCallback(() => setFetchCount((c) => c + 1), []);

  return { rules, isLoading, error, refetch };
}
